import React, { useState, useEffect } from "react";
import Map from "../../components/Map";
import Detail from "../../components/Detail";
import Clock from "../../components/Clock";

import searchIcon from "/assets/icon/Search.svg";
import pump from "/assets/pump.png";
import gate from "/assets/gate.png";
import eyeIcon from "/assets/Eye.png";

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

function WastewaterPage() {
  const [isPumpOn, setIsPumpOn] = useState(false);
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [gatePercentage, setGatePercentage] = useState(0);

  const handlePumpToggle = () => {
    setIsPumpOn(!isPumpOn);
  };

  const handleGateToggle = () => {
    setIsGateOpen(!isGateOpen);
  };

  const increaseGatePercentage = () => {
    setGatePercentage((prev) => (prev < 100 ? prev + 10 : 100));
  };

  const decreaseGatePercentage = () => {
    setGatePercentage((prev) => (prev > 0 ? prev - 10 : 0));
  };

  useEffect(() => {
    if (!isGateOpen) {
      setGatePercentage(0);
    }
  }, [isGateOpen]);

  const parameters = [
    {
      name: "PH",
      height: "50%",
      color: "linear-gradient(to top, #0E2560, #FFFFFF)",
    },
    {
      name: "DO",
      height: "70%",
      color: "linear-gradient(to top, #FF007A, #FFFFFF)",
    },
    {
      name: "EC",
      height: "80%",
      color: "linear-gradient(to top, #14BC25, #FFFFFF)",
    },
    {
      name: "TDS",
      height: "60%",
      color: "linear-gradient(to top, #FF13E7, #FFFFFF)",
    },
    {
      name: "SAL",
      height: "55%",
      color: "linear-gradient(to top, #FF7A00, #FFFFFF)",
    },
    {
      name: "TEMP",
      height: "75%",
      color: "linear-gradient(to top, #33CCCC, #FFFFFF)",
    },
    {
      name: "TUR",
      height: "65%",
      color: "linear-gradient(to top, #E50E0E, #FFFFFF)",
    },
  ];

  const createGradient = (ctx, chartArea, color1, color2) => {
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(0, color1); // สีที่ด้านล่าง
    gradient.addColorStop(1, color2); // สีที่ด้านบน
    return gradient;
  };

  const data = {
    labels: [
      "1 am",
      "2 am",
      "3 am",
      "4 am",
      "5 am",
      "6 am",
      "7 am",
      "8 am",
      "9 am",
      "10 am",
      "11 am",
      "12 pm",
    ],
    datasets: [
      {
        label: "เข้า",
        data: [2, 3, 2.5, 3, 4, 4.5, 5, 4, 3.5, 4, 3, 2.5],
        borderColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return;
          }

          return createGradient(ctx, chartArea, "#A9CBFF", "#0500FF");
        },
        backgroundColor: "transparent",
        tension: 0,
        pointBackgroundColor: "#0500FF",
        pointBorderColor: "white",
        pointRadius: 0,
        fill: true,
      },
      {
        label: "ออก",
        data: [1, 1.5, 2, 2.5, 3, 3.5, 4, 3.5, 3, 3.5, 3, 2],
        borderColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return;
          }

          return createGradient(ctx, chartArea, "#FFA6EB", "#FF007A");
        },
        backgroundColor: "transparent",
        tension: 0,
        pointBackgroundColor: "#FF007A",
        pointBorderColor: "white",
        pointRadius: 0,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false, // ทำให้กราฟเต็มพื้นที่ที่กำหนด
    plugins: {
      legend: {
        display: true,
        position: "top", // ย้ายเลเจนด์ไปด้านบน
        align: "end", // จัดชิดขวา
        labels: {
          color: "white",
          usePointStyle: true, // ใช้รูปแบบของจุด
          pointStyle: "circle", // เปลี่ยนเป็นวงกลม
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: "white",
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "เวลา",
          color: "white",
        },
      },
      y: {
        beginAtZero: true,
        max: 5,
        ticks: {
          color: "white",
        },
        grid: {
          color: "rgba(255, 255, 255, 0.2)",
        },
        title: {
          display: true,
          text: "Level(m)",
          color: "white",
          padding: { top: 0, left: 0, right: 0, bottom: 10 },
        },
      },
    },
  };

  const tableData = [
    {
      location: "พระนคร",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "ดุสิต",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "บางรัก",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "บางเขน",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "พระโขนง",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "ยานนาวา",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "พญาไท",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "ธนบุรี",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
    {
      location: "สาทร",
      time: "10:00",
      pH: 6.6,
      DO: 5.5,
      EC: 250,
      TDS: 1000,
      SAL: 900,
      TEMP: 25,
      TUR: 5,
      Level: 2.5,
      BOD: 5.5,
    },
  ];

  return (
    <div className="wastewater-page">
      <div className="sections1">
        <h1>น้ำเสีย</h1>
        <h2>
          <Clock />
        </h2>
      </div>

      <div className="sections2">
        <div className="area-chart-container">
          <div className="divWasteater">
            <strong>สถานะปั๊ม</strong>
            <img
              src={pump}
              alt="pump"
              className={`pump-img ${isPumpOn ? "" : "gray"}`}
            />
            <div className="compact-toggle-switch">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isPumpOn}
                  onChange={handlePumpToggle}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
          <div className="divWasteater">
            <strong>ประตู</strong>
            <img
              src={gate}
              alt="gate"
              className={`gate-img ${isGateOpen ? "" : "gray"}`}
            />
            <div className="compact-toggle-switch">
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isGateOpen}
                  onChange={handleGateToggle}
                />
                <span className="slider"></span>
              </label>
            </div>
            <div className="gate-percentage-control">
              <button onClick={decreaseGatePercentage} disabled={!isGateOpen}>
                -
              </button>
              <span>{gatePercentage}%</span>
              <button onClick={increaseGatePercentage} disabled={!isGateOpen}>
                +
              </button>
            </div>
          </div>
        </div>
        <div className="areaMap">
          <Map />
        </div>
      </div>

      <div className="sections3wastewater">
        <div className="area-chart-container">
          <div className="divWasteater">
            <strong>พระนคร</strong>{" "}
            <div className="parameter-container">
              {parameters.map((param, index) => (
                <div className="parameter" key={index}>
                  <span className="parameter-name">{param.name}</span>
                  <div className="bar">
                    <div
                      className="fill"
                      style={{ height: param.height, background: param.color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="area-chart-container">
          <div className="divWasteater">
            {" "}
            <div className="line-chart-container">
              <Line data={data} options={options} />
            </div>
          </div>
        </div>
      </div>

      <div className="data-list">
        <div className="search-bar">
          <input type="text" placeholder="ค้นหารหัสเสา ชื่อเสา ตำแหน่งติดตั้ง" />
        </div>
        <div className="tablecontainer">
          <table>
            <thead>
              <tr>
                <th>ที่ตั้ง</th>
                <th>เวลา</th>
                <th>pH (pH unit)</th>
                <th>DO (mg/L)</th>
                <th>EC (μS/cm)</th>
                <th>TDS (mg/L)</th>
                <th>SAL (ppt)</th>
                <th>Temp (°C)</th>
                <th>TUR (NTU)</th>
                <th>Level (m)</th>
                <th>BOD (mg/L)</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, index) => (
                <tr key={index}>
                  <td>{row.location}</td>
                  <td>{row.time}</td>
                  <td>{row.pH}</td>
                  <td>{row.DO}</td>
                  <td>{row.EC}</td>
                  <td>{row.TDS}</td>
                  <td>{row.SAL}</td>
                  <td>{row.TEMP}</td>
                  <td>{row.TUR}</td>
                  <td>{row.Level}</td>
                  <td>{row.BOD}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WastewaterPage;
